/**
 * Which payments are actually in the bank.
 *
 * Jonathon, 2026-09-06: "what counts as paid should be when that specific money
 * hits our bank account in mercury." That rule has been the balance's rule ever
 * since — and it was only half kept, because one of the two things the balance
 * counted was not money in the bank at all.
 *
 * `mercury_payment_requests.state = 'paid'` is **Mercury's answer about an
 * invoice**, not a credit on the account. For a bank transfer the difference is
 * hours. For a card it is two or three days: `Paid` means the charge succeeded
 * at Stripe, which then holds the money and pays it out in a batch later
 * (`stripeBatch.ts`). A job whose customer paid by card on Friday read as fully
 * settled, and the money was not there until Tuesday.
 *
 * Josh's plan of 2026-09-12 put the choice to Jonathon, who took it: money in
 * the account is the final word, even for cards. So:
 *
 *   **Submitted** — the payment has been taken. Mercury's `Paid`, or its
 *   "processing", ticks this. It is the honest name for what is known.
 *
 *   **Check**, and the balance — the money is *there*. Only a confirmed credit
 *   on the Mercury account, or cash and a cleared check handed to a rep, say
 *   that (ADR 0139).
 *
 * ## The middle payments stay a person's

 * Jonathon, same day: "if there are values in the 2nd and 3rd box i want those
 * to be manually marked paid since those are very rare." A second or third draw
 * is a staged payment on a job that was sold in four, it has no checklist boxes
 * of its own, and it is rare enough that nobody has a feel for what an
 * automatic mistake there would look like. So the first and the final settle
 * themselves; the middle two are offered to a person and wait.
 */

/** The slots that may be settled with no person: the deposit and the balance. */
import { linksWhoseCreditLanded } from "./linksWhoseCreditLanded.js";

export const SLOTS_THAT_SETTLE_THEMSELVES = [1, 4] as const;

/**
 * May this slot be confirmed against a job without anybody pressing anything?
 *
 * First and final only. Everything else — a second or third draw — is recorded
 * the moment a person says so and not before.
 */
export function settlesWithoutAPerson(slot: number | null): boolean {
  /*
   * A payment on no schedule settles itself too.
   *
   * A link raised by hand for an amount somebody typed (ADR 0141) has no slot,
   * and Jonathon's answer when asked what should happen when one is paid was
   * to work it out from the amount: if it is the deposit or the balance, that
   * box ticks; if it is an odd figure, the money is recorded against the job
   * and nothing ticks. That decision is `matchPayment`'s, and it is safe to
   * let it run precisely because it ticks nothing it cannot account for.
   */
  if (slot === null) return true;
  return (SLOTS_THAT_SETTLE_THEMSELVES as readonly number[]).includes(
    Number(slot),
  );
}

/** One payment request on a job, as the callers below read it. */
export interface RequestOnTheJob {
  state: string;
  amountCents: number | null;
  /** When Mercury first said the customer's payment was processing. */
  paymentStartedAt?: string | null;
  /** Which of the four this is, or null for a link raised by hand. */
  slot?: number | null;
  /**
   * Whether this payment's money has been **confirmed** in the bank.
   *
   * Not merely claimed. A bank line is claimed before it is confirmed, and a
   * confirmation that never finishes leaves the claim behind; reading the
   * claim as arrival billed the customer for their deposit a second time
   * (Josh, 2026-09-14).
   */
  landed?: boolean;
  /** A link somebody raised by hand, which is on no schedule (ADR 0141). */
  isManual?: boolean | null;
  /**
   * A person said this link's money is already in the bank, and recorded it
   * so (ADR 0312). The balance counts that record, so the link is no longer
   * on its way, whatever the bank has shown yet.
   */
  saidInTheBank?: boolean;
}

/**
 * Money on this job that settled no scheduled payment.
 *
 * A link raised by hand (ADR 0141) is on no schedule, so none of the slot
 * arithmetic can see it: half a balance a customer wanted to split, a change
 * order, a repair billed after the fact. Jonathon, 2026-09-13, asked what a
 * paid one should do — it comes off what they owe.
 *
 * **Read off the money, not off the request.** The first version counted any
 * manual request whose state said `paid`, and Josh found two faults with that
 * on the same day: it is Mercury's word rather than the bank's, which is the
 * whole of ADR 0139; and when the amount happened to match a scheduled payment
 * the credit ticked that slot *as well*, so the same money was counted twice
 * and a job with $6,630 still owing read as settled.
 *
 * A confirmed credit that ticked no slot is money on the job that the slots do
 * not account for — once, whatever raised it.
 *
 * ## What it is worth
 *
 * **What the link billed**, where a link billed it. Josh, 2026-09-14: "the 3%
 * card surcharge comes off what the customer owes." A card link for $5,000
 * bills $5,150 and the card is charged $5,150, so `chargeCents` is $5,150 —
 * and crediting that against the balance hands the customer $150 of roof they
 * did not buy. The fee is not part of the contract (see balance.ts) so it
 * cannot be part of what settles it. `billedCents` is the request's own
 * amount, which has the surcharge in a column of its own.
 *
 * `chargeCents` is still the fallback, for money nobody raised a link for:
 * there is no surcharge on it, and a card payment lands net of the processor's
 * cut, which is a cost of the job (ADR 0095) rather than a debt of the
 * customer's.
 *
 * ## What it does not count
 *
 * **Money handed over in person, arriving at the bank.** Josh, same day: "cash
 * gets counted twice once it's banked." A check or cash recorded on the job is
 * already money in hand from the moment it is recorded (ADR 0125), and the
 * credit that appears days later when somebody walks it to the bank is that
 * same money. Counted here as well, a $6,630 check pays for $13,260 of roof.
 * `handedOver` is the caller saying this credit is that: a check deposit, or a
 * credit a recorded payment already points at.
 */
export function offScheduleMoney(
  deposits: readonly {
    slots?: readonly number[] | null;
    amountCents: number | null;
    chargeCents?: number | null;
    /** What the link that raised this billed, before any card surcharge. */
    billedCents?: number | null;
    /** This credit is money already recorded on the job (ADR 0125). */
    handedOver?: boolean;
  }[],
): number {
  return deposits
    .filter((deposit) => (deposit.slots ?? []).length === 0)
    .filter((deposit) => !deposit.handedOver)
    .reduce((sum, deposit) => {
      const billed = Math.round(Number(deposit.billedCents ?? 0));
      if (billed > 0) return sum + billed;
      const charged = Math.round(Number(deposit.chargeCents ?? 0));
      const landed = Math.round(Number(deposit.amountCents ?? 0));
      return sum + (charged > 0 ? charged : landed);
    }, 0);
}

/**
 * The payment request a credit confirmed against a job is the money for.
 *
 * `offScheduleMoney` credits what a link *billed* only when the credit knows
 * its request, and until round 4 of #235 only the automatic paths told it — a
 * bank line naming its invoice (ADR 0142) and a Stripe charge naming its
 * customer (ADR 0138). A person confirming a line, or a part of a payout they
 * had just split, tied nothing, so the credit fell back to what the card was
 * charged: a $6,630 card link on a $13,260 job read as $6,431.10 owed, the
 * $198.90 surcharge counted as contract money (Josh, 2026-09-15).
 *
 * The job is already decided — a person chose it, or an identifier did — so
 * this asks one narrower question: which request on that job billed exactly
 * what the customer was charged for this credit? Amount plus surcharge, to the
 * cent, on a request that was actually sent and is not yet tied to other
 * money. Where the credit settled a slot, that slot's request is preferred;
 * where it settled none, a link on no schedule is.
 *
 * A request this credit already holds is returned as it is, so a confirmation
 * run twice ties nothing new.
 */
export function requestThisCreditPays(
  requests: readonly {
    id: string;
    state: string;
    slot?: number | null;
    amountCents: number | null;
    surchargeCents?: number | null;
    /** The credit this request is already tied to, if any. */
    depositId?: string | null;
  }[],
  credit: {
    id: string;
    /** What the customer was charged for it: gross, surcharge included. */
    chargedCents: number;
    /** The slots the credit settled. */
    slots?: readonly number[] | null;
  },
): string | null {
  const already = requests.find((request) => request.depositId === credit.id);
  if (already) return already.id;

  const charged = Math.round(Number(credit.chargedCents));
  if (!(charged > 0)) return null;
  const fits = requests.filter(
    (request) =>
      !request.depositId &&
      (request.state === "sent" || request.state === "paid") &&
      Math.round(Number(request.amountCents ?? 0)) +
        Math.round(Number(request.surchargeCents ?? 0)) ===
        charged,
  );
  const settled = (credit.slots ?? []).map(Number);
  const preferred = fits.filter((request) =>
    settled.length > 0
      ? request.slot != null && settled.includes(Number(request.slot))
      : request.slot == null,
  );
  const pool = preferred.length > 0 ? preferred : fits;
  // Paid before sent: a paid request is the one whose money is travelling.
  return (
    pool.find((request) => request.state === "paid")?.id ?? pool[0]?.id ?? null
  );
}

/**
 * The cash or cleared check a credit confirmed against a job is.
 *
 * Josh, 2026-09-14: "cash gets counted twice once it's banked." Money handed
 * over in person counts from the moment it is recorded (ADR 0125) by ticking
 * its slot; the credit that appears when somebody walks it to the bank is the
 * same money, and `offScheduleMoney` counted it a second time because nothing
 * said so. Round 4 of #235: $5,000 cash on a $13,260 job read as $3,260 owed
 * in the app, on the board and in the letter, not $8,260.
 *
 * So a credit for exactly the amount of a recorded payment on the same job,
 * already paid and not yet tied to a bank line, is that payment. Only one that
 * ticked a slot, or one toward a payment plan: any other recorded payment with
 * no slot is counted nowhere by the balance, so the credit is the one place
 * that money counts, and tying it would make it count nowhere at all. A plan
 * payment is counted at what was handed over (ADR 0286), so its credit is
 * the same money a second time, like a slot's (ADR 0288).
 *
 * A payment this credit already holds is returned as it is, so a confirmation
 * run twice ties nothing new.
 *
 * A link's money a person said was in the bank is a recorded payment too
 * (ADR 0312), and one off the schedule names no slot. It is counted by
 * `linkMoneySaidInTheBank` rather than nowhere, so it may be tied like a
 * slot's: the credit is then that record's money and is not counted again.
 *
 * A credit confirmed for slots is only for a payment that shares one: on a
 * 50/50 schedule the First and the Final are one amount, and the First's
 * credit must not take the Final's transfer (review of #937). A credit with
 * no slots is anybody's. Both directions ask this: confirming a credit, and
 * It's in the bank (`creditThisPaymentIs`).
 */
export function recordedPaymentThisCreditIs(
  payments: readonly {
    id: string;
    state: string;
    amountCents: number | null;
    slots?: readonly number[] | null;
    /** Recorded toward an in-house payment plan (ADR 0286). */
    towardPlan?: boolean;
    /** The credit this payment is already tied to, if any. */
    depositId?: string | null;
    /** The link this records the money for, said in the bank (ADR 0312). */
    mercuryRequestId?: string | null;
  }[],
  credit: {
    id: string;
    amountCents: number;
    /** The scheduled payments it was confirmed for, if any. */
    slots?: readonly number[] | null;
  },
): string | null {
  const already = payments.find((payment) => payment.depositId === credit.id);
  if (already) return already.id;

  const amount = Math.round(Number(credit.amountCents));
  if (!(amount > 0)) return null;
  const creditSlots = (credit.slots ?? []).map(Number);
  const sharesASlot = (slots: readonly number[] | null | undefined) =>
    creditSlots.length === 0 ||
    (slots ?? []).some((slot) => creditSlots.includes(Number(slot)));
  return (
    payments.find(
      (payment) =>
        !payment.depositId &&
        payment.state === "paid" &&
        ((payment.slots ?? []).length > 0 ||
          payment.towardPlan === true ||
          Boolean(payment.mercuryRequestId)) &&
        sharesASlot(payment.slots) &&
        Math.round(Number(payment.amountCents ?? 0)) === amount,
    )?.id ?? null
  );
}

/**
 * Has the customer started paying this?
 *
 * - `paid`, or a payment Mercury says is processing (`payment_started_at`) —
 *   they have done it. The money may be days from the bank (a card sits at
 *   Stripe), and that is exactly the gap ADR 0139 opened: the balance stops
 *   counting it as paid, so without this the final invoice would bill it a
 *   second time.
 * - `sent` with nothing started — a link in their inbox, and **not** money on
 *   its way. Until 2026-09-18 a sent link counted as started, so the moment
 *   the final link went out the job card read "$0 left, $13,260 on its way —
 *   paid, not yet in the bank" about money nobody had paid (Josh, review
 *   round 4 of #233).
 * - failed or cancelled — money nobody has, whatever was started.
 * - anything else — nobody is paying it, so it belongs in the next ask.
 *
 * Jonathon, 2026-09-13, asked whether the final bill should subtract a card
 * deposit that is paid but not yet in the bank: "only if that first payment
 * has been initiated. if it hasn't been initiated then need to bill for the
 * whole thing at that point."
 */
export function paymentInitiated(request: RequestOnTheJob): boolean {
  /*
   * Cancelled and failed are money nobody has.
   *
   * `payment_started_at` is stamped the first time Mercury says a payment is
   * processing and never cleared, so an ACH that bounced — or one a rep called
   * off — kept saying "started" for ever and went on coming off the final
   * bill. Josh found that on 2026-09-13; Jonathon, asked whether a bounced
   * payment should still reduce the bill: no, bill it again.
   */
  if (request.state === "cancelled" || request.state === "failed") return false;
  if (request.state === "paid") return true;
  return Boolean(request.paymentStartedAt);
}

/**
 * What a customer has already started paying that the bank has not seen.
 *
 * Subtracted from the balance when the final payment is raised, so the one
 * number a customer reads is never money they have already sent.
 */
export function alreadyBeingCollected(
  requests: readonly RequestOnTheJob[],
  /** The slots the balance has already treated as paid. */
  paidSlots: readonly number[] = [],
): number {
  const paid = new Set(paidSlots.map(Number));
  return requests
    .filter((request) => {
      if (!paymentInitiated(request)) return false;
      /*
       * Money the balance has already subtracted is not subtracted twice.
       *
       * `outstanding` is the contract less what has arrived. Taking a payment
       * off it again because a request also says `paid` is how the ordinary
       * job stopped being billed at all: a $13,260 job with its $6,630 deposit
       * paid and confirmed had $6,630 outstanding and $6,630 subtracted, so
       * the final request was worth nothing and never went out (Josh,
       * 2026-09-13).
       *
       * What is left here is exactly what this is for: money on its way that
       * the bank has not seen. `landed` has to mean confirmed rather than
       * claimed, or a confirmation that fell over halfway leaves a payment
       * that is neither counted by the balance nor subtracted here, and the
       * final invoice asks for it again (Josh, 2026-09-14).
       */
      if (request.landed) return false;
      if (request.saidInTheBank) return false;
      if (request.slot != null && paid.has(Number(request.slot))) return false;
      /*
       * A link raised by hand counts only once the customer has paid it.
       *
       * An unpaid change-order link is money nobody has, and subtracting it
       * would bill the customer less than they owe for work they have not paid
       * for (Jonathon, 2026-09-13). A paid one whose money has not landed is
       * on its way, like any other.
       */
      if (request.isManual && request.state === "sent") return false;
      return true;
    })
    .reduce(
      (sum, request) => sum + Math.round(Number(request.amountCents ?? 0)),
      0,
    );
}

/** Where a claim that a slot is paid can come from. */
export interface MoneySeen {
  /**
   * Slots on credits confirmed against this job — the bank's own lines,
   * matched to a payment by a person, by an identifier Mercury put on the
   * line, or by Stripe naming who was inside a payout.
   */
  confirmedDeposits: readonly (readonly number[])[];
  /**
   * Slots on cash, and on checks that have cleared. The one kind of paid that
   * never passes through Mercury (ADR 0125).
   */
  handedOver: readonly number[];
  /**
   * Slots whose Mercury payment request says `paid`.
   *
   * **Not counted, deliberately.** Passed in so that the rule is stated where
   * it is tested rather than by the absence of a query in the app: this is the
   * number Roof Board used to call paid, and the reason it no longer does is
   * the whole of ADR 0139.
   */
  requestsMercuryCallsPaid?: readonly number[];
}

/**
 * The slots whose money has actually arrived.
 *
 * Deduplicated and sorted, because one payment can be seen twice — as a
 * confirmed credit and as a check somebody also recorded — and a slot is paid
 * once however many sources say so.
 */
export function slotsInTheBank(seen: MoneySeen): number[] {
  const paid = new Set<number>();
  for (const slots of seen.confirmedDeposits) {
    for (const slot of slots) {
      const n = Number(slot);
      if (Number.isFinite(n)) paid.add(n);
    }
  }
  for (const slot of seen.handedOver) {
    const n = Number(slot);
    if (Number.isFinite(n)) paid.add(n);
  }
  return [...paid].sort((a, b) => a - b);
}

/**
 * Money a person said was in the bank for a link on no schedule, counted
 * until the bank shows the link's own credit (ADR 0312).
 *
 * A link on the schedule is counted through its slot: the record names the
 * slot, `slotsInTheBank` reads it, and the credit arriving later names the
 * same slot, so the money is counted once however many say so. A link raised
 * by hand names no slot (ADR 0141), and a recorded payment with no slot is
 * counted nowhere by the balance — so this is where that record counts, at
 * what the link billed. The moment the link's credit is confirmed,
 * `offScheduleMoney` counts the credit instead and this stops, which is the
 * same hand-over a check makes from the record to the deposit.
 *
 * Unless the record holds a credit of its own. A credit the bank shows
 * without naming the link (a person confirmed it, or its figure matched no
 * link) is tied to the record instead (`recordedPaymentThisCreditIs`), and is
 * then handed over and not counted by `offScheduleMoney`: the record goes on
 * counting, and the money is counted once either way.
 *
 * The letter transcribes this as `linkMoneyAPersonBanked`, pinned by
 * supabase/test/payment-letter-balance-match.test.ts.
 */
export function linkMoneySaidInTheBank(
  payments: readonly {
    state: string;
    amountCents: number | null;
    slots?: readonly number[] | null;
    towardPlan?: boolean;
    /** The link this record is the money for. */
    mercuryRequestId?: string | null;
    /** The credit this record is tied to, if any. */
    depositId?: string | null;
  }[],
  requests: readonly { id: string; mercuryDepositId?: string | null }[],
  confirmedDepositIds: ReadonlySet<string>,
): number {
  const landed = linksWhoseCreditLanded(requests, confirmedDepositIds);
  return payments
    .filter((payment) => payment.state === "paid")
    .filter((payment) => (payment.slots ?? []).length === 0)
    .filter((payment) => payment.towardPlan !== true)
    .filter(
      (payment) =>
        Boolean(payment.mercuryRequestId) &&
        (Boolean(payment.depositId) ||
          !landed.has(payment.mercuryRequestId as string)),
    )
    .reduce(
      (sum, payment) =>
        sum + Math.max(0, Math.round(Number(payment.amountCents ?? 0))),
      0,
    );
}
